import React, { useEffect, useMemo, useRef } from "react";
import Color from "colorjs.io";
import { cn } from "@/lib/utils";
import { Send, Plus, X } from "lucide-react";
import type { WidgetChatStyle, WidgetActionButtonStyle } from "@/types/ui/branding";

const DEFAULT_BRAND = "#4E9CFF";
const DEFAULT_SECONDARY = "#FF4DB8";

interface AttachmentChip {
  id: string;
  name: string;
  previewUrl?: string;
  status?: "analyzing" | "ready" | "error";
  analysisText?: string;
}

interface GenieChatInputWebProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  attachments?: AttachmentChip[];
  hasAttachments?: boolean;
  onRemoveAttachment?: (id: string) => void;
  onOpenFilePicker?: () => void;
  className?: string;
  autoFocus?: boolean;
  /** Agent brand color — drives gradient border + send button + soft tints. */
  brandColor?: string;
  /** Optional explicit secondary color (from agent customBranding).
   *  If absent, derived as an analogous hue shift of brandColor. */
  secondaryColor?: string | null;
  /** Input pill background color override. Falls back to white. */
  backgroundColor?: string | null;
  /** Border/placeholder/plus-button/send-button overrides. Embed widget only. */
  chatStyle?: WidgetChatStyle | null;
  /** Action-button (mic/chat/mute/etc) color overrides — reused for the send
   *  button when chatStyle.sendButtonUseActionStyle is set. */
  actionButtonStyle?: WidgetActionButtonStyle | null;
}

/** Derive an analogous color (~30° hue shift) so single-color brands still
 *  produce a visible gradient. Falls back to brandColor itself on parse error. */
export const deriveSecondary = (brandColor: string): string => {
  try {
    const base = new Color(brandColor);
    const hsl = base.to("hsl");
    const [h, s, l] = hsl.coords;
    const shifted = new Color("hsl", [
      (h + 35 + 360) % 360,
      Math.min(100, s + 8),
      Math.max(35, Math.min(70, l - 6)),
    ]);
    return shifted.toString({ format: "hex" });
  } catch {
    return brandColor;
  }
};

const hexToRgb = (hex: string): [number, number, number] => {
  const m = hex.replace("#", "");
  return [
    parseInt(m.substring(0, 2), 16) || 0,
    parseInt(m.substring(2, 4), 16) || 0,
    parseInt(m.substring(4, 6), 16) || 0,
  ];
};

export const GenieChatInputWeb = ({
  value,
  onChange,
  onSend,
  disabled,
  attachments = [],
  hasAttachments = false,
  onRemoveAttachment,
  onOpenFilePicker,
  className,
  autoFocus,
  brandColor = DEFAULT_BRAND,
  secondaryColor,
  backgroundColor,
  chatStyle,
  actionButtonStyle,
}: GenieChatInputWebProps) => {
  const gradientColors = useMemo(() => {
    const start = brandColor || DEFAULT_BRAND;
    const end = secondaryColor || (start === DEFAULT_BRAND ? DEFAULT_SECONDARY : deriveSecondary(start));
    return { start, end };
  }, [brandColor, secondaryColor]);

  const gradient = `linear-gradient(to right, ${gradientColors.start}, ${gradientColors.end})`;
  const [r, g, b] = hexToRgb(gradientColors.start);
  const glowShadow = `0 8px 30px -12px rgba(${r}, ${g}, ${b}, 0.45)`;
  const sendButtonShadow = `0 4px 14px -4px rgba(${r}, ${g}, ${b}, 0.5)`;
  const softBrandBg = `rgba(${r}, ${g}, ${b}, 0.08)`;
  const softBrandBgHover = `rgba(${r}, ${g}, ${b}, 0.14)`;

  // Border color override(s) — falls back to the brand gradient. A single
  // borderColor with no borderColor2 renders as a solid ring instead of a
  // gradient, matching how the CTA/header color overrides behave elsewhere.
  const borderStart = chatStyle?.borderColor || gradientColors.start;
  const borderEnd = chatStyle?.borderColor2 || (chatStyle?.borderColor ? chatStyle.borderColor : gradientColors.end);
  const borderGradient = `linear-gradient(to right, ${borderStart}, ${borderEnd})`;

  const plusColor = chatStyle?.plusButtonColor || gradientColors.start;
  const [pr, pg, pb] = hexToRgb(plusColor);
  const plusBg = `rgba(${pr}, ${pg}, ${pb}, 0.08)`;
  const plusBgHover = `rgba(${pr}, ${pg}, ${pb}, 0.14)`;

  const useActionStyleForSend = chatStyle?.sendButtonUseActionStyle;
  const sendIdleBg = useActionStyleForSend ? actionButtonStyle?.idleBackgroundColor || softBrandBg : undefined;
  const sendActiveBg = useActionStyleForSend ? actionButtonStyle?.activeBackgroundColor || gradientColors.start : undefined;
  const sendIconColor = useActionStyleForSend ? actionButtonStyle?.iconColor : undefined;

  const canSend = !!value.trim() || hasAttachments || attachments.length > 0;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Delay slightly to let drawer animation complete on mobile
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  return (
    <div className={cn("w-full", className)} style={{ boxSizing: "border-box" }}>
      {attachments.length > 0 && onRemoveAttachment && (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-1 rounded-full bg-white/80 border border-[#E5EAF0] px-2 py-1 shadow-sm"
            >
              {/* The chip has always been filename-only: the original line read
                  att.file.previewUrl, and AttachmentChip has no `file`, so it
                  never compiled and shipped commented out. att.previewUrl is
                  the field that exists. onError hides it so a revoked object
                  URL shows nothing rather than a broken-image glyph. */}
              {att.previewUrl && (
                <img
                  src={att.previewUrl}
                  alt=""
                  className="h-4 w-4 flex-shrink-0 rounded-sm object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <span className="max-w-[140px] truncate text-xs text-[#4B5563]">
                {att.name}
              </span>
              <button
                type="button"
                onClick={() => onRemoveAttachment(att.id)}
                className="text-[10px] text-[#9CA3AF] hover:text-[#4B5563]"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Agent brand gradient-border wrapper (start = brandColor, end = secondaryColor
          or derived analogous shade). Same canonical gradient pattern, just brand-driven.
          chatStyle.hideBorder drops it to a plain (no border) pill. */}
      <div
        className="rounded-full"
        style={
          chatStyle?.hideBorder
            ? undefined
            : { padding: "1.5px", background: borderGradient, boxShadow: glowShadow }
        }
      >
        <div
          className="flex items-center gap-2 rounded-full px-2 py-1.5"
          style={{ backgroundColor: backgroundColor || "white" }}
        >
          {onOpenFilePicker && (
            <button
              type="button"
              onClick={onOpenFilePicker}
              className="flex h-9 w-9 items-center justify-center rounded-full transition-colors flex-shrink-0"
              style={{ backgroundColor: plusBg, color: plusColor }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = plusBgHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = plusBg; }}
              aria-label="Attach image"
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!disabled && canSend) {
                  onSend();
                }
              }
            }}
            placeholder="Type a message..."
            className="flex-1 bg-transparent text-base text-[#222B45] outline-none min-w-0 px-1 chat-input-placeholder"
            style={{ fontSize: 16, "--placeholder-color": chatStyle?.placeholderColor || "#9CA3AF" } as React.CSSProperties}
          />
          <button
            type="button"
            onClick={() => {
              if (!disabled && canSend) {
                onSend();
              }
            }}
            disabled={disabled || !canSend}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-full flex-shrink-0 transition-all",
              canSend && !disabled ? "hover:scale-105" : "",
            )}
            style={
              useActionStyleForSend
                ? {
                    backgroundColor: canSend && !disabled ? sendActiveBg : sendIdleBg,
                    boxShadow: canSend && !disabled ? sendButtonShadow : undefined,
                  }
                : canSend && !disabled
                  ? { background: gradient, boxShadow: sendButtonShadow }
                  : { backgroundColor: softBrandBg }
            }
            aria-label="Send message"
          >
            <Send
              className={cn("w-4 h-4", !useActionStyleForSend && (canSend && !disabled ? "text-white" : "text-[#9CA3AF]"))}
              style={
                useActionStyleForSend
                  ? { color: canSend && !disabled ? sendIconColor || "white" : sendIconColor || "#9CA3AF" }
                  : undefined
              }
            />
          </button>
        </div>
      </div>
      <style>{`
        .chat-input-placeholder::placeholder {
          color: var(--placeholder-color, #9CA3AF);
        }
      `}</style>
    </div>
  );
};

